import { useForm } from "react-hook-form";

import {
  useErrors,
  useLoginPasswordlessEmailCode,
  useResend,
  useScreen,
} from "@auth0/auth0-acul-react/login-passwordless-email-code";

import BeneficiaryLayout from "@/components/BeneficiaryLayout";
import { ULThemeFloatingLabelField } from "@/components/form/ULThemeFloatingLabelField";
import { ULThemeFormMessage } from "@/components/form/ULThemeFormMessage";
import { Form, FormField, FormItem } from "@/components/ui/form";
import { ULThemeButton } from "@/components/ULThemeButton";
import { ULThemeAlert, ULThemeAlertTitle } from "@/components/ULThemeError";
import ULThemeLink from "@/components/ULThemeLink";
import {
  readSignupDetails,
  signupDetailsAsUlpFields,
} from "@/utils/beneficiary";
import { executeSafely } from "@/utils/helpers/executeSafely";
import { applyCareSuperBrand } from "@/utils/theme/careSuperBrand";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";

// The email code screen, for both sign-up and returning login (Auth0 uses the
// same screen for both; passwordless sign-up is implicit on the first verified
// code). Observed live: /u/login/passwordless-email-challenge.
//
// Only the Beneficiary Claims client reaches it: it's the only client with the
// passwordless `email` connection, and the renderer filter on client metadata
// pins it anyway.
//
// SIGN-UP DETAILS RIDE ON THIS SUBMIT. This POST is the one that creates the
// user, so the name and mobile captured on signup-id are re-sent here as
// ulp-* fields, read from sessionStorage (utils/beneficiary.ts). The
// Beneficiary Profile post-login Action writes them to the profile. On a
// returning login nothing is carried and nothing extra is sent.
interface CodeValues {
  code: string;
}

function BeneficiaryEmailCode() {
  const emailCode = useLoginPasswordlessEmailCode();
  const screen = useScreen();
  const { errors, hasError, dismiss } = useErrors();
  const { remaining, disabled, startResend } = useResend({
    timeoutSeconds: 30,
  });

  applyAuth0Theme(emailCode);
  applyCareSuperBrand();
  document.title = "Enter your code | CareSuper Beneficiary Claims";

  const form = useForm<CodeValues>({ defaultValues: { code: "" } });
  const {
    formState: { isSubmitting },
  } = form;

  const email = screen.data?.email;
  const generalErrors = errors.byType("auth0").filter((err) => !err.field);
  const codeSDKError = errors.byField("code")[0]?.message;

  const onSubmit = async ({ code }: CodeValues) => {
    await executeSafely("Submit beneficiary email code", () =>
      emailCode.submitCode({
        code: code.trim(),
        ...signupDetailsAsUlpFields(readSignupDetails()),
      })
    );
  };

  return (
    <BeneficiaryLayout
      title="Check your email"
      subtitle={
        email ? (
          <>
            We've sent a 6-digit code to <strong>{email}</strong>.
          </>
        ) : (
          "We've sent a 6-digit code to your email."
        )
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {hasError && generalErrors.length > 0 && (
            <div className="space-y-3 mb-4">
              {generalErrors.map((error) => (
                <ULThemeAlert
                  key={error.id}
                  variant="destructive"
                  onDismiss={() => dismiss(error.id)}
                >
                  <ULThemeAlertTitle>{error.message}</ULThemeAlertTitle>
                </ULThemeAlert>
              ))}
            </div>
          )}

          <FormField
            control={form.control}
            name="code"
            rules={{
              required: "Enter the code from the email",
              validate: (v: string) =>
                /^\d{6}$/.test(v.trim()) ? true : "The code is 6 digits",
            }}
            render={({ field, fieldState }) => (
              <FormItem>
                <ULThemeFloatingLabelField
                  {...field}
                  label="Code*"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  error={!!fieldState.error || !!codeSDKError}
                />
                <ULThemeFormMessage
                  sdkError={codeSDKError}
                  hasFormError={!!fieldState.error}
                />
              </FormItem>
            )}
          />

          <ULThemeButton
            type="submit"
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Checking…" : "Continue"}
          </ULThemeButton>
        </form>
      </Form>

      <div className="text-center space-y-4 mt-6">
        <p className="text-body-text text-(length:--ul-theme-font-body-text-size) font-body">
          The code expires in 5 minutes.{" "}
          <ULThemeButton
            type="button"
            variant="link"
            size="link"
            disabled={disabled}
            onClick={() => startResend()}
          >
            {disabled ? `Send a new code in ${remaining}s` : "Send a new code"}
          </ULThemeButton>
        </p>
        {screen.editIdentifierLink ? (
          <p>
            <ULThemeLink href={screen.editIdentifierLink}>
              Use a different email
            </ULThemeLink>
          </p>
        ) : null}
      </div>
    </BeneficiaryLayout>
  );
}

export default BeneficiaryEmailCode;
